import { Interview, InterviewType, Subject } from "types/graphqlapi";
import { isEmailExistQuery } from "utils/apis/user";
import { getYMDStr } from "utils/common";
import * as Yup from "yup";

// const TEXT_INPUT_MAX_BASIC = 30;
const TEXT_AREA_MAX_BASIC = 150;

export const validationSchemaUser = Yup.object({
  name: Yup.string().required("必須入力項目です。入力してください。"),
  email: Yup.string()
    .email("正しいメールアドレス形式で入力してください。")
    .required("必須入力項目です。入力してください。")
    .test(
      "email",
      "同じメールアドレスが既に使用されています。",
      async function (value) {
        if (this.parent.id != "" && this.parent.id != undefined) return true;
        try {
          const result = await isEmailExistQuery(value as string);
          return !result;
        } catch {
          alert("エラーが発生しました。");
        }
        return false;
      }
    ),
});

export const validationSchemaGroup = Yup.object({
  name: Yup.string().required("必須入力項目です。入力してください。"),
});

export const validationSchemaSubject = (interviewList: Interview[]) =>
  Yup.object({
    group_id: Yup.string().required(
      "必須入力項目です。グループを選択してください。"
    ),
    subject_number:
      Yup.string().required("必須入力項目です。入力してください。"),
    subject_name: Yup.string().required("必須入力項目です。入力してください。"),
    input_expiration_date: Yup.date()
      .notRequired()
      .test(
        "input_expiration_date",
        "本日または実査の最後の終了日以降の日付を選択してください",
        function (date) {
          if (date === undefined) return true;
          if (interviewList && interviewList.length !== 0) {
            const endDates = interviewList.map(
              (interview) => new Date(interview.scheduled_end_date as string)
            );
            const latestEndDate = endDates.reduce((latestDate, currentDate) =>
              currentDate > latestDate ? currentDate : latestDate
            );
            return date.getTime() > latestEndDate.getTime();
          } else {
            return date?.getTime() > new Date().getTime();
          }
        }
      ),
    subject_description: Yup.string().max(
      TEXT_AREA_MAX_BASIC,
      `${TEXT_AREA_MAX_BASIC}文字以内です。`
    ),
    subject_purpose: Yup.string().max(
      TEXT_AREA_MAX_BASIC,
      `${TEXT_AREA_MAX_BASIC}文字以内です。`
    ),
    main_user_id_group: Yup.string().test(
      "main_user_id_group",
      "グループ名を選択してください。",
      function (value) {
        const group_id = this.parent.group_id;
        if (!value && !group_id) {
          return false;
        } else return true;
      }
    ),
    memo: Yup.string().max(
      TEXT_AREA_MAX_BASIC,
      `${TEXT_AREA_MAX_BASIC}文字以内です。`
    ),
  });

export const validationSchemaInterview = (
  selectedSubjectType: InterviewType,
  subject: Subject
) =>
  Yup.object({
    scheduled_start_date: Yup.date()
      .required("必須入力項目です。入力してください。")
      .test(
        "scheduled_start_date_compared_with_expiration_date",
        "案件データ削除日以降の日付を設定することはできません",
        async function (value) {
          if (value === undefined) return false;
          if (
            subject?.input_expiration_date &&
            value.getTime() >=
              new Date(subject.input_expiration_date as string).getTime()
          ) {
            return false;
          } else {
            return true;
          }
        }
      ),
    scheduled_start_time:
      Yup.string().required("必須入力項目です。入力してください。"),

    ...(selectedSubjectType === InterviewType.TERM
      ? {
          scheduled_end_date: Yup.date()
            .required("必須入力項目です。入力してください。")
            .test(
              "scheduled_end_date",
              "開始日よりも前の日付を設定することはできません。",
              function (value) {
                try {
                  if (value === undefined) return true;
                  const endDate = getYMDStr(value);
                  const startDate = getYMDStr(this.parent.scheduled_start_date);
                  if (endDate < startDate) {
                    return false;
                  }
                  return true;
                } catch (e) {
                  return false;
                }
              }
            )
            .test(
              "scheduled_end_date_compared_with_expiration_date",
              "案件データ削除日以降の日付を設定することはできません",
              async function (value) {
                if (value === undefined) return false;
                if (
                  subject?.input_expiration_date &&
                  value.getTime() >=
                    new Date(subject.input_expiration_date as string).getTime()
                ) {
                  return false;
                } else {
                  return true;
                }
              }
            ),
          scheduled_end_time: Yup.string()
            .required("必須入力項目です。入力してください。")
            .test(
              "scheduled_end_time",
              "開始日時 < 終了日時に設定してください。",
              function (value) {
                if (value === undefined) return true;
                try {
                  const endDateTime = new Date(
                    `${getYMDStr(this.parent.scheduled_end_date)}T${value}`
                  );
                  const startDateTime = new Date(
                    `${getYMDStr(this.parent.scheduled_start_date)}T${
                      this.parent.scheduled_start_time
                    }`
                  );
                  if (endDateTime <= startDateTime) {
                    return false;
                  }
                  return true;
                } catch (e) {
                  return false;
                }
              }
            ),
        }
      : {
          interview_minutes:
            Yup.number().required("必須入力項目です。入力してください。"),
        }),

    memo: Yup.string().max(
      TEXT_AREA_MAX_BASIC,
      `${TEXT_AREA_MAX_BASIC}文字以内です。`
    ),
  });

export const validationSchemaFileResourceUrl = Yup.object({
  url: Yup.string()
    .required("必須入力項目です。入力してください。")
    .url("正しいURL形式で入力してください。"),
});

export const validationSchemaFileResourceFile = Yup.object({
  file: Yup.mixed().required("ファイルがアップロードされていません"),
  title: Yup.string().required("必須入力項目です。入力してください。"),
});

export const validationSchemaMonitor = () =>
  Yup.object({
    memo: Yup.string().max(
      TEXT_AREA_MAX_BASIC,
      `${TEXT_AREA_MAX_BASIC}文字以内です。`
    ),
  });

export const validationSchemaLogin = Yup.object({
  email: Yup.string().email("不正なメールアドレス").required("未入力"),
  password: Yup.string().required("未入力"),
});

export const validationSchemaConfirm = Yup.object({
  code: Yup.string()
    .required("未入力")
    .matches(/^[0-9]+$/, "数字以外が入力されています")
    .min(6, "PINコードは6桁です")
    .max(6, "PINコードは6桁です"),
  email: Yup.string().email("不正なメールアドレス").required("未入力"),
  password: Yup.string()
    .required("未入力")
    .min(8, "パスワードは8文字以上である必要があります"),
  passwordConfirm: Yup.string().when("password", (password, schema) => {
    return schema.test({
      test: (passwordConfirm: string) => password === passwordConfirm,
      message: "パスワードが一致しません。",
    });
  }),
});

export const validationSchemaPasswordChange = Yup.object({
  oldpassword: Yup.string().required("必須入力項目です。入力してください。"),
  password1: Yup.string().required("必須入力項目です。入力してください。"),
  password2: Yup.string()
    .required("必須入力項目です。入力してください。")
    .test(
      "password2",
      "パスワードとパスワード（確認）が一致しません。",
      function (value) {
        if (value != this.parent.password1) {
          return false;
        }
        return true;
      }
    ),
});

// function getMonthBefore() {
//   const monthBefore = new Date();
//   monthBefore.setHours(0);
//   monthBefore.setMinutes(0);
//   monthBefore.setSeconds(0);
//   monthBefore.setMilliseconds(0);
//   monthBefore.setMonth(monthBefore.getMonth() - 1);
//   return monthBefore;
// }
